import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLang } from "../i18n";

const Row = ({ name, provider, purpose, duration, type }) => (
  <tr className="border-b border-border">
    <td className="py-3 pr-4 font-mono text-xs font-semibold text-ink">{name}</td>
    <td className="py-3 pr-4 font-body text-sm text-ink/70">{provider}</td>
    <td className="py-3 pr-4 font-body text-sm text-ink/70">{purpose}</td>
    <td className="py-3 pr-4 font-body text-sm text-ink/70">{duration}</td>
    <td className="py-3 font-body text-sm">
      <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${type === "tech" ? "bg-secondary text-ink" : "bg-lime-accent text-ink"}`}>
        {type === "tech" ? "Tecnico" : "Analytics"}
      </span>
    </td>
  </tr>
);

export default function CookiePolicy() {
  const { lang } = useLang();
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = lang === "it" ? "Cookie Policy | BOXLY" : "Cookie Policy | BOXLY";
  }, [lang]);
  const it = lang === "it";

  return (
    <main className="bg-white pb-24 pt-28 sm:pt-32" data-testid="cookie-page">
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <Link to="/" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-ink/60 transition-colors hover:text-electric-blue" data-testid="back-home">
          <ArrowLeft className="h-3.5 w-3.5" /> {it ? "Torna alla home" : "Back to home"}
        </Link>
        <h1 className="mt-6 font-display text-4xl font-black leading-[0.95] text-ink sm:text-5xl">Cookie Policy</h1>
        <p className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-ink/50">
          {it ? "Ultimo aggiornamento" : "Last updated"}: 12/2026
        </p>

        <div className="mt-12 space-y-8 font-body text-base leading-relaxed text-ink/70">
          <p>{it
            ? "Il sito utilizza esclusivamente cookie tecnici necessari al corretto funzionamento del sito e, in modo separato e opzionale, strumenti di analisi/registrazione sessione forniti dalla piattaforma di hosting a fini diagnostici. Non vengono utilizzati cookie pubblicitari, di profilazione marketing né sistemi di tracciamento cross-site."
            : "The site uses only technical cookies necessary for its correct operation and, separately and optionally, analytics/session recording tools provided by the hosting platform for diagnostic purposes. No advertising, marketing profiling or cross-site tracking cookies are used."}
          </p>
          <p>{it
            ? "Alla prima visita viene mostrato un banner che consente di accettare o rifiutare gli strumenti di analisi non essenziali. La preferenza è memorizzata localmente e può essere modificata in qualsiasi momento eliminando le preferenze salvate dal browser."
            : "On first visit a banner is shown to accept or reject non-essential analytics tools. The preference is stored locally and can be changed at any time by clearing the browser's stored preferences."}
          </p>

          <h2 className="pt-4 font-sans text-xl font-bold text-ink sm:text-2xl">{it ? "Elenco cookie e tecnologie utilizzate" : "Cookies and technologies used"}</h2>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[640px] text-left">
              <thead className="bg-secondary">
                <tr>
                  <th className="p-3 font-mono text-xs uppercase tracking-wider text-ink/60">Nome</th>
                  <th className="p-3 font-mono text-xs uppercase tracking-wider text-ink/60">Provider</th>
                  <th className="p-3 font-mono text-xs uppercase tracking-wider text-ink/60">{it ? "Finalità" : "Purpose"}</th>
                  <th className="p-3 font-mono text-xs uppercase tracking-wider text-ink/60">{it ? "Durata" : "Duration"}</th>
                  <th className="p-3 font-mono text-xs uppercase tracking-wider text-ink/60">{it ? "Tipo" : "Type"}</th>
                </tr>
              </thead>
              <tbody>
                <Row name="boxly-lang" provider="boxlyitalia.it" purpose={it ? "Memorizza la lingua selezionata" : "Stores selected language"} duration={it ? "Persistente" : "Persistent"} type="tech" />
                <Row name="boxly-cookie-consent" provider="boxlyitalia.it" purpose={it ? "Memorizza la scelta di consenso cookie" : "Stores cookie consent choice"} duration={it ? "6 mesi" : "6 months"} type="tech" />
                <Row name="ph_* (PostHog)" provider="Emergent / PostHog" purpose={it ? "Analisi diagnostica e registrazione sessione anonima (previo consenso)" : "Diagnostic analytics and anonymous session recording (with consent)"} duration={it ? "12 mesi" : "12 months"} type="analytics" />
              </tbody>
            </table>
          </div>

          <h2 className="pt-4 font-sans text-xl font-bold text-ink sm:text-2xl">{it ? "Servizi terzi che caricano risorse esterne" : "Third-party services loading external resources"}</h2>
          <ul className="ml-5 list-disc space-y-1">
            <li>{it ? "Fontshare — CDN caratteri tipografici (nessun cookie, potrebbe registrare l'IP per motivi tecnici)." : "Fontshare — font CDN (no cookies, may log IP for technical reasons)."}</li>
            <li>{it ? "Provider di infrastruttura cloud/hosting utilizzato dal sito." : "Cloud/hosting infrastructure provider used by the site."}</li>
          </ul>

          <h2 className="pt-4 font-sans text-xl font-bold text-ink sm:text-2xl">{it ? "Come modificare le preferenze" : "How to change preferences"}</h2>
          <p>{it
            ? "È possibile modificare o revocare il consenso in qualsiasi momento cancellando i dati del sito dal browser. Alla successiva visita verrà nuovamente proposto il banner di consenso."
            : "You can change or withdraw consent at any time by clearing site data from the browser. On your next visit the consent banner will be shown again."}
          </p>

          <p>{it ? "Per ulteriori informazioni: " : "For further information: "}
            <a href="mailto:info@boxlyitalia.it" className="text-electric-blue underline">info@boxlyitalia.it</a>.
          </p>
        </div>
      </div>
    </main>
  );
}
